import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  MailOutline,
  ShieldOutlined,
  Timer,
} from '@mui/icons-material';
import React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { IConfig } from '../../Interfaces/IConfig';
import { TwoFactorAuthInput } from '../core/inputs/TwoFactorAuthInput';
import CountdownSoftLock from './CountdownSoftLock';
import type { LoginAuthResponse, SendAuthFn } from './LoginDriver';

interface FormValues {
  twoFactorCode: string;
  shouldSaveDevice: boolean;
}

interface IProps {
  msg: string;
  isLoading: boolean;
  idContact: number;
  tmpSessionToken: string;
  sendFunction: SendAuthFn;
  configObject: IConfig;
  softLockTime: null | string;
  setSoftLockTime: React.Dispatch<React.SetStateAction<string | null>>;
}

const defaultValues: FormValues = {
  twoFactorCode: '',
  shouldSaveDevice: false,
};

export const LoginF2A: React.FC<IProps> = ({
  msg,
  isLoading,
  idContact,
  tmpSessionToken,
  sendFunction,
  configObject,
  softLockTime,
  setSoftLockTime,
}) => {
  const theme = useTheme();
  const methods = useForm<FormValues>({
    defaultValues,
  });

  const handleAfterSave = (res: LoginAuthResponse) => {
    if (res.detail !== undefined) {
      localStorage.setItem('soft_lock_till', res.detail);
      setSoftLockTime(res.detail);
    } else {
      localStorage.setItem('SessionToken', res.SessionToken ?? '');
      localStorage.setItem(
        'TimestampSessionToken',
        res.TimestampSessionToken ?? ''
      );
      localStorage.setItem('idContact', String(res.idContact ?? ''));
      localStorage.setItem('role', res.Role ?? '');
      localStorage.setItem('scope', res.Scope ?? '');
      localStorage.setItem(
        'passwordResetRequired',
        res.passwordResetRequired ?? 'false'
      );

      if (res.DeviceToken !== undefined) {
        localStorage.setItem('DeviceToken', res.DeviceToken);
      }

      window.location.reload();
    }
  };

  const onSubmit = (data: FormValues) => {
    sendFunction(
      {
        idContact: idContact,
        TempSessionToken: tmpSessionToken,
        '2FACode': data.twoFactorCode,
        SaveDevice: data.shouldSaveDevice,
      },
      handleAfterSave,
      () => methods.reset(defaultValues)
    );
  };

  React.useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      const pastedText = event.clipboardData?.getData('text') || '';
      const digits = pastedText.replace(/\D/g, '').slice(0, 6);

      if (!digits.length) return;

      methods.setValue('twoFactorCode', digits);

      const input = document.querySelector(
        "input[name='twoFactorCode']"
      ) as HTMLInputElement | null;

      input?.focus();
    };

    window.addEventListener('paste', handlePaste as EventListener);

    return () => {
      window.removeEventListener('paste', handlePaste as EventListener);
    };
  }, [methods]);

  return (
    <FormProvider {...methods}>
      <Box
        component='form'
        noValidate
        onSubmit={methods.handleSubmit(onSubmit)}
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: { xs: 4, md: 6 },
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 540 }}>
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            <Stack spacing={3} alignItems='center'>
              {/* Logo */}
              <Box
                component='img'
                alt={`${configObject.company_name} Logo`}
                src={`data:${configObject.logo_mime};base64,${configObject.logo}`}
                sx={{
                  width: 'auto',
                  maxWidth: 280,
                  maxHeight: 100,
                  objectFit: 'contain',
                }}
              />

              {/* Header */}
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    mx: 'auto',
                    mb: 2,
                    borderRadius: '50%',
                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ShieldOutlined
                    sx={{ color: 'primary.main', fontSize: 30 }}
                  />
                </Box>
                <Typography variant='h4' sx={{ mb: 1 }}>
                  Authentifizierung
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ maxWidth: 380, mx: 'auto' }}
                >
                  Wir haben Ihnen einen 6-stelligen Code per E-Mail gesendet.
                  Bitte geben Sie diesen unten ein, um Ihre Anmeldung zu
                  bestätigen.
                </Typography>
              </Box>

              {/* Code input */}
              <Box sx={{ width: '100%' }}>
                <TwoFactorAuthInput name='twoFactorCode' required />
              </Box>

              {/* Hinweis-Chips */}
              <Stack
                direction='row'
                spacing={2}
                useFlexGap
                flexWrap='wrap'
                justifyContent='center'
                sx={{ color: 'text.secondary' }}
              >
                <Stack direction='row' spacing={0.5} alignItems='center'>
                  <Timer sx={{ fontSize: 16 }} />
                  <Typography variant='caption'>1 Stunde gültig</Typography>
                </Stack>
                <Stack direction='row' spacing={0.5} alignItems='center'>
                  <MailOutline sx={{ fontSize: 16 }} />
                  <Typography variant='caption'>
                    Spam-Ordner prüfen
                  </Typography>
                </Stack>
              </Stack>

              {/* Save device */}
              <Box
                sx={{
                  width: '100%',
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                  backgroundColor: alpha(theme.palette.primary.main, 0.03),
                }}
              >
                <Controller
                  name='shouldSaveDevice'
                  control={methods.control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={e => field.onChange(e.target.checked)}
                        />
                      }
                      label={
                        <Box>
                          <Typography
                            variant='body2'
                            sx={{ fontWeight: 600, lineHeight: 1.3 }}
                          >
                            Dieses Gerät 30 Tage merken
                          </Typography>
                          <Typography
                            variant='caption'
                            color='text.secondary'
                          >
                            Sie melden sich auf diesem Gerät anschließend ohne
                            erneute 2-Faktor-Abfrage an.
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: 'center', m: 0, gap: 1 }}
                    />
                  )}
                />
              </Box>

              {/* Submit */}
              <Button
                type='submit'
                variant='contained'
                color='primary'
                fullWidth
                size='large'
                disabled={isLoading}
                startIcon={
                  isLoading ? (
                    <CircularProgress size={18} color='inherit' />
                  ) : undefined
                }
              >
                {/* Eigenes span: Eine Browser-Übersetzung ersetzt lose
                    Textknoten, dann scheitert das Einfügen des Lade-Symbols
                    davor (#1337). */}
                <span>{isLoading ? 'Anmeldung läuft …' : 'Anmelden'}</span>
              </Button>

              {/* Error / Soft-Lock */}
              <Box sx={{ width: '100%', minHeight: softLockTime !== null || msg !== '' ? 60 : 0 }}>
                {softLockTime !== null ? (
                  <CountdownSoftLock
                    key={`softlock-${softLockTime}`}
                    targetTime={new Date(String(softLockTime))}
                  />
                ) : (
                  msg !== '' && <Alert severity='error'>{msg}</Alert>
                )}
              </Box>

              {/* Footer-Hinweis */}
              <Typography
                variant='caption'
                color='text.disabled'
                sx={{ textAlign: 'center' }}
              >
                Bitte verlassen Sie diese Seite nicht — andernfalls muss ein
                neuer Code angefordert werden.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </FormProvider>
  );
};
